import "./styles.scss";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainCard from "../../components/MainCard/MainCard";
import Table from "../../components/Table/Table";
import { IRowMyClientsReports, IClientCSVData, IMyClientsParams } from "../../constants/interfaces";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { RemoveRedEyeOutlined } from "@mui/icons-material";
import { ClientsStore } from "../../store/MyClientsStore";
import { ROUTE_PATHS } from "../../constants/routePaths";
import { Column, columnTypes, TableOptions } from "../../components/DataTable/datatable.models";
import { Box } from "@mui/material";
import { getSnapshot, Instance } from "mobx-state-tree";
import { ContextModel } from "../../models/Common";
import { DateTime } from "luxon";
import IClientReports from "../../assets/svg/IClientReports";

const CancellationRisk = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prevParams = location.state && location.state.keepParams ? getSnapshot(ClientsStore.params) : undefined;
  const pageWasRendered = useRef(false);
  const [loadingTable, setLoadingTable] = useState<boolean>(true);
  const [param, setParam] = useState<IMyClientsParams>({
    endDate: prevParams ? prevParams.endDate : undefined,
  });
  const [rows, setRows] = useState<IRowMyClientsReports[] | []>([]);
  const [context, setContext] = useState<Instance<typeof ContextModel> | null>();

  /**
   * columns object to be passed to the Table component with the settings for each one.
   */
  const columns: Column[] = [
    {
      name: "Full Name",
      value: "fullName",
      sortable: false,
      mobile: true,
    },
    {
      name: "Last Login Date",
      value: "lastLoginDate",
      type: columnTypes.DATE,
      sortable: false,
      mobile: true,
    },
    {
      name: "FW Start Date",
      value: "startDate",
      sortable: false,
      mobile: false,
    },
    {
      name: "Email",
      value: "email",
      sortable: false,
      mobile: false,
    },
    {
      name: "Phone",
      value: "phone",
      sortable: false,
      mobile: false,
    },
    {
      name: "",
      value: "",
      type: columnTypes.CUSTOM,
      renderCell: () => <RemoveRedEyeOutlined sx={{ fontSize: "2rem" }} className="icon-column" />,
      mobile: true,
    },
  ];

  const onRowClicked = (row: object) => {
    navigate(`${ROUTE_PATHS.MY_CLIENTS_TABS.replace(":id", (row as IRowMyClientsReports)?.id.toString())}`, {
      state: { backLinkText: "Client Report", backLinkUrl: ROUTE_PATHS.CANCELLATION_RISK_REPORT },
    });
  };

  const getDate = (date: DateTime | null) => {
    setParam({
      ...param,
      endDate: date?.toISODate() ?? undefined,
    });
  };


  const getDataForCSV = async (): Promise<IClientCSVData[]> => {
    const paramToCSV = { ...param, page: undefined, size: undefined, withoutPagination: true };
    const data = await ClientsStore.fetchClientReports(paramToCSV, "cancellation-risk", true);
    return data ?? [];
  };

  const onTableOptionsChanged = (tableParams: TableOptions) => {
    //save table pagination
    setParam({
      ...param,
      page: tableParams.page,
      size: tableParams.pageSize,
    });
  };

  const getCancellationRisk = async () => {
    try {
      const result = await ClientsStore.fetchCancellationRiskReport(param);
      setRows(result.items);
      setContext(result.context);
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  useEffect(() => {
    ClientsStore.clear();
    const fetchData = async () => {
      setLoadingTable(true);
    };
    fetchData();
    pageWasRendered.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pageWasRendered.current) {
      const fetchData = async () => {
        setLoadingTable(true);
        try {
          await getCancellationRisk();
        } catch (error) {
          console.log(error);
        } finally {
          setLoadingTable(false);
        }
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param]);

  useEffect(() => {
    pageWasRendered.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainCard>
      <Table
        getCSVData={getDataForCSV}
        idTable="cancellation-risk-table"
        header={
          <Box
            sx={{
              padding: 1.25,
              fontSize: "1.4rem",
              textAlign: "left",
              marginBottom: 0.5,
              borderBottom: 2,
              borderColor: "divider",
            }}
          >
            <span style={{ fontWeight: "bold", fontSize: "16px" }}>Cancellation Risk Report:</span>
            {" Clients who have not recently logged in to the app."}
          </Box>
        }
        tableProps={{
          columns: columns,
          data: rows,
          loading: loadingTable,
          totalRows: context?.totalRows ?? 0,
          handleTableOptionsChanged: onTableOptionsChanged,
          handleRowClicked: onRowClicked,
          expandRowOnMobileBreakpoint: true,
          mobileBreakpoint: 900,
          paginationValue:
            prevParams && prevParams.page && prevParams.size
              ? {
                  page: prevParams.page - 1,
                  size: prevParams.size,
                }
              : undefined,
        }}
        calendarProps={{
          withSingleCalendar: true,
          sx: { maxWidth: "20rem" },
          onSingleDateChange: getDate,
          allowFuture: true,
          singleLabel: "End Date",
          singleTitle: "Last Login Date Before:",
          singleDate: prevParams?.endDate ? DateTime.fromISO(prevParams.endDate) : undefined,
        }}
        downloadCSVButtonProps={{
          id: "client-btn-dowload",
          show: true,
          text: "Download",
          disabled: rows?.length === 0 ? true : false,
          startIcon: <SaveAltIcon />,
          variant: "outlined",
          size: "large",
        }}
        buttonsProps={[
          {
            id: "client-btn-reports",
            show: true,
            text: "Client Reports",
            startIcon: <IClientReports width="2rem" height="2rem" />,
            variant: "outlined",
            size: "large",
            className: "five-inputs",
            onClick: () => {
              navigate(`${ROUTE_PATHS.CLIENT_REPORTS}`);
            },
          },
        ]}
      />
    </MainCard>
  );
};

export default CancellationRisk;
